import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import PageCard from './components/PageCard';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import AcaoModal from './components/modalServices/AcaoModal';
import usePermissions from './components/hooks/usePermissions';
import useProgramaSituacao from './components/hooks/useProgramaSituacao';

function Acoes() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('acoes');
  
  // Verificação de situação do programa
  const { isProgramaAtivo } = useProgramaSituacao();
  
  const [acoes, setAcoes] = useState([]);
  const [atualizar, setAtualizar] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAcoes, setSelectedAcoes] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filtroGeral, setFiltroGeral] = useState('');
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');

  // Filtrar dados
  const acoesFiltradas = [...acoes].filter(a => {
    if (!filtroGeral) return true;
    const termo = filtroGeral.trim().toLowerCase();
    const descricao = `${a.descricao || ''}`.toLowerCase();
    const programa = `${a.descricaoPrograma || ''}`.toLowerCase();

    return descricao.includes(termo) || programa.includes(termo);
  });

  // Hierarquizar ações por programa
  const hierarquizarAcoes = acoes => {
    if (!Array.isArray(acoes)) {
      return {};
    }

    const hierarquia = {};

    acoes.forEach(acao => {
      const programaId = acao.idPrograma;

      // Inicializar programa se não existir
      if (!hierarquia[programaId]) {
        hierarquia[programaId] = {
          programa: {
            id: programaId,
            descricao: acao.descricaoPrograma,
          },
          acoes: [],
        };
      }

      // Adicionar ação
      hierarquia[programaId].acoes.push(acao);
    });

    return hierarquia;
  };

  const acoesHierarquizadas = hierarquizarAcoes(acoesFiltradas);

  const toggleProgram = programaId => {
    setExpandedPrograms(prev => ({
      ...prev,
      [programaId]: !prev[programaId],
    }));
  };

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    fetchAcao();
  }, [atualizar, token, navigate, podeAbrir, carregandoPermissoes]);

  function fetchAcao() {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.ACAO}/find_all`, {
        params: {
          cnpjAssociacao: cnpj,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setAcoes(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter acao:', error);
        navigate('/');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const handleSelectRecord = record => {
    const recordKey = `${record.cnpjAssociacao}-${record.idPrograma}-${record.id}`;

    if (selectedRecord && selectedRecord.key === recordKey) {
      setSelectedRecord(null);
      setIsProgramaInativo(false);
    } else {
      setSelectedRecord({ key: recordKey, data: record });
      // Verificar se o programa está encerrado
      const programaAtivo = isProgramaAtivo(record.idPrograma);
      setIsProgramaInativo(!programaAtivo);
    }
  };

  const getSelectedRecord = () => {
    return selectedRecord ? selectedRecord.data : null;
  };

  const hasSelectedRecord = selectedRecord !== null;

  function handleDelete(acao) {
    if (!acao) return;

    axios
      .delete(
        `${API_BASE_URL.ACAO}/delete/${acao.cnpjAssociacao}/${acao.idPrograma}/${acao.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then(result => {
        setAtualizar(result);
        setSelectedRecord(null);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir acao:', error);
        if (error.response) {
          const status = error.response.status;
          const message = error.response.data;

          if (status === 409) {
            showDefaultModal('error', message);
          } else {
            showDefaultModal('error', message);
          }
        } else {
          showDefaultModal('error', 'Erro de conexão com o servidor!');
        }
      });
  }

  const handleDeleteSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      showConfirmModal('delete', () => handleDelete(record));
    }
  };

  const handleEditSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      setSelectedAcoes(record);
      setShowModal(true);
    }
  };

  function handleNewAcao() {
    setSelectedAcoes(null);
    setShowModal(true);
  }

  function handleSuccess() {
    setAtualizar([]);
    setShowModal(false);
    setSelectedRecord(null);
  }

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Ações Cadastradas"
      icon="bi-diagram-3"
      scrollable={false}
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Ações.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
        <>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            onClick={handleNewAcao}
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar ações' : 'Nova ação'}
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            onClick={handleEditSelected}
            disabled={!podeEditar || !hasSelectedRecord || isProgramaInativo}
            title={!podeEditar ? 'Você não tem permissão para editar' : !hasSelectedRecord ? 'Selecione um registro' : isProgramaInativo ? 'Programa ENCERRADO - não é possível editar' : 'Editar registro selecionado'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>
          <div className="vr mx-1" />
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            onClick={handleDeleteSelected}
            disabled={!podeExcluir || !hasSelectedRecord || isProgramaInativo}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !hasSelectedRecord ? 'Selecione um registro' : isProgramaInativo ? 'Programa ENCERRADO - não é possível excluir' : 'Excluir registro selecionado'}
          >
            <i className="bi bi-trash me-1" />
            Excluir
          </button>
          {hasSelectedRecord && (
            <div className="d-flex align-items-center ms-3 px-3 py-1 bg-warning-subtle border border-warning rounded">
              <i className="bi bi-check-circle-fill text-warning me-2"></i>
              <small className="text-dark fw-medium">
                SELECIONADO:{' '}
                <span className="fw-bold">
                  {getSelectedRecord()?.descricao}
                </span>
              </small>
            </div>
          )}
          <div className="ms-auto d-flex align-items-center">
            <div
              className="input-group"
              style={{ maxWidth: 300, height: 31 }}
            >
              <span
                className="input-group-text bg-white border-end-0 px-2"
                style={{ height: 31 }}
              >
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{
                  textTransform: 'uppercase',
                  height: 31,
                  fontSize: 13,
                }}
                placeholder="Pesquisar..."
                value={filtroGeral}
                onChange={e => setFiltroGeral(e.target.value.toUpperCase())}
              />
              {filtroGeral && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltroGeral('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th className="px-2 align-middle">Descrição</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                Object.values(acoesHierarquizadas)
                  .sort((a, b) => a.programa.id - b.programa.id)
                  .map(programaData => (
                    <React.Fragment key={programaData.programa.id}>
                      {/* Linha do Programa */}
                      <tr
                        className="table-success fw-bold cursor-pointer"
                        onClick={() => toggleProgram(programaData.programa.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td title="Programa - Nível superior da hierarquia">
                          <i
                            className={`bi ${
                              expandedPrograms[programaData.programa.id]
                                ? 'bi-folder-minus'
                                : 'bi-folder-plus'
                            } me-2`}
                          ></i>
                          {programaData.programa.descricao} (
                          {programaData.acoes.length} AÇÕES)
                        </td>
                      </tr>

                      {/* Ações do Programa */}
                      {expandedPrograms[programaData.programa.id] &&
                        programaData.acoes
                          .sort((a, b) => a.id - b.id)
                          .map(acao => {
                            const recordKey = `${acao.cnpjAssociacao}-${acao.idPrograma}-${acao.id}`;
                            const isSelected =
                              selectedRecord &&
                              selectedRecord.key === recordKey;

                            return (
                              <tr
                                key={recordKey}
                                className={`${
                                  isSelected ? 'table-warning' : ''
                                } cursor-pointer`}
                                onClick={() => handleSelectRecord(acao)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td
                                  className="ps-4"
                                  title="Ação - Elemento executável da hierarquia"
                                >
                                  <i className="bi bi-file-earmark me-2 text-primary"></i>
                                  {acao.descricao}
                                </td>
                              </tr>
                            );
                          })}
                    </React.Fragment>
                  ))}
              {!isLoading && acoesFiltradas.length === 0 && (
                <tr>
                  <td className="text-center">Nenhum registro encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />

      {/* Modal para edição/criação de acao */}
      <AcaoModal
        show={showModal}
        onHide={() => setShowModal(false)}
        acao={selectedAcoes}
        onSuccess={handleSuccess}
      />
      </>
      )}
    </PageCard>

  );
}

export default Acoes;
